import { apiFetch, readErrorMessage, readJsonArray } from '../api/client';

export interface MountCapabilities {
  canWrite: boolean;
  canSearch: boolean;
  canChunkUpload: boolean;
  canUnzip: boolean;
}

export interface MountSummary {
  id: string;
  displayName: string;
  kind: 'Local' | 'Smb' | 'S3' | 'OneDrive';
  mode: 'Read' | 'ReadWrite';
  health?: string | null;
  healthReason?: string | null;
  capabilities?: MountCapabilities | null;
}

export interface BrowseItem {
  name: string;
  path: string;
  isDirectory: boolean;
  hasSubDirectories: boolean;
  size: number | null;
  dateModified: string | null;
  dateCreated: string | null;
}

function qs(params: Record<string, string | undefined>): string {
  const parts = Object.entries(params).filter(([, value]) => value !== undefined && value !== '');
  if (parts.length === 0) return '';
  return `?${parts.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value!)}`).join('&')}`;
}

/** files = My Storage + OneDrive; spr = Public + DB mounts; all = everything (Assistant default). */
export type StorageMountCatalog = 'files' | 'spr' | 'all';

export interface ListMountsOptions {
  catalog?: StorageMountCatalog;
}

export async function listMounts(options?: ListMountsOptions): Promise<MountSummary[]> {
  const catalog = options?.catalog;
  const res = await apiFetch(
    `/api/files/mounts${qs({ catalog: catalog && catalog !== 'all' ? catalog : undefined })}`
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<MountSummary>(res);
}

export async function listItems(mountId: string, path?: string): Promise<BrowseItem[]> {
  const res = await apiFetch(`/api/files/${encodeURIComponent(mountId)}/items${qs({ path })}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<BrowseItem>(res);
}

export function downloadFileUrl(mountId: string, path: string): string {
  return `/api/files/${encodeURIComponent(mountId)}/download?path=${encodeURIComponent(path)}`;
}

export function previewFileUrl(mountId: string, path: string): string {
  return `/api/files/${encodeURIComponent(mountId)}/preview?path=${encodeURIComponent(path)}`;
}

export async function resolveMicrosoft365Url(mountId: string, path: string): Promise<string> {
  const res = await apiFetch(
    `${mountApiBase(mountId)}/microsoft-365-url?path=${encodeURIComponent(path)}`
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
  const payload = (await res.json()) as { webUrl?: unknown };
  if (typeof payload.webUrl !== 'string' || !payload.webUrl.startsWith('https://')) {
    throw new Error('Microsoft 365 did not return a valid link.');
  }
  return payload.webUrl;
}

export function mountApiBase(mountId: string): string {
  return `/api/files/${encodeURIComponent(mountId)}`;
}

export function deleteInfoUrl(mountId: string, path: string): string {
  return `${mountApiBase(mountId)}/delete-info?path=${encodeURIComponent(path)}`;
}

export function transferUrl(): string {
  return '/api/files/transfer';
}

export function transferJobUrl(jobId: string): string {
  return `/api/files/transfer-jobs/${encodeURIComponent(jobId)}`;
}

export function zipUrl(mountId: string): string {
  return `${mountApiBase(mountId)}/zip`;
}

export function downloadZipUrl(mountId: string): string {
  return `${mountApiBase(mountId)}/download-zip`;
}

export function buildSearchUrl(
  mountId: string,
  query: string,
  path?: string,
  maxResults = 100
): string {
  return `${mountApiBase(mountId)}/search${qs({
    q: query,
    path,
    maxResults: String(maxResults)
  })}`;
}

export function buildFolderSummaryUrl(mountId: string, path?: string): string {
  return `${mountApiBase(mountId)}/summary${qs({ path })}`;
}

export function buildUploadChunkUrl(mountId: string): string {
  return `${mountApiBase(mountId)}/upload-chunk`;
}

export function buildUnzipUrl(mountId: string): string {
  return `${mountApiBase(mountId)}/unzip`;
}

export async function searchItems(
  mountId: string,
  query: string,
  path?: string,
  maxResults = 100
): Promise<BrowseItem[]> {
  const res = await apiFetch(buildSearchUrl(mountId, query, path, maxResults));
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<BrowseItem>(res);
}

export interface FolderSummary {
  path: string;
  fileCount: number;
  folderCount: number;
  totalBytes: number;
}

export async function getFolderSummary(mountId: string, path?: string): Promise<FolderSummary> {
  const res = await apiFetch(buildFolderSummaryUrl(mountId, path));
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return (await res.json()) as FolderSummary;
}
